import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { ThemeContext } from '../context/ThemeContext';
import { API_BASE } from '../api';

function DesignerDashboard() {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showWorkModal, setShowWorkModal] = useState(false);
  const [workFormData, setWorkFormData] = useState({
    thumbnail_link: '',
    designer_notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const navigate = useNavigate();
  const { currentTheme } = useContext(ThemeContext);


  // 🎨 GLOBAL COLORS DEFINITION
  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    whiteText: '#FFFFFF',
  };

  useEffect(() => {
    fetchDesignerTasks();
  }, []);

  useEffect(() => {
    filterTasks();
  }, [tasks, searchTerm, statusFilter]);

  const fetchDesignerTasks = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      // Get designer ID from session storage
      const designerId = sessionStorage.getItem('userId');
      
      if (!designerId) {
        throw new Error('Designer ID not found. Please login again.');
      }

      console.log('🔄 Fetching designer tasks for ID:', designerId);

      const response = await fetch(`${API_BASE}/get_designer_tasks.php?designer_id=${designerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📊 Full API response:', data);
      
      if (data.success) {
        let tasksData = [];
        
        // Handle different response structures
        if (data.data && Array.isArray(data.data.tasks)) {
          tasksData = data.data.tasks;
        } else if (Array.isArray(data.data)) {
          tasksData = data.data;
        } else if (Array.isArray(data.tasks)) {
          tasksData = data.tasks;
        } else if (Array.isArray(data)) {
          tasksData = data;
        } else {
          console.warn('⚠️ Unexpected data structure:', data);
          throw new Error('Invalid tasks data format received from server');
        }

        console.log('✅ Tasks data loaded:', tasksData);
        console.log('📋 Number of tasks:', tasksData.length);
        
        if (tasksData.length > 0) {
          console.log('🔍 First task sample:', tasksData[0]);
        }

        setTasks(tasksData);
      } else {
        throw new Error(data.message || 'Failed to fetch tasks');
      }
      
    } catch (error) {
      console.error('❌ Error fetching designer tasks:', error);
      setMessage('❌ Error loading tasks: ' + error.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // Search and filter functionality
  const filterTasks = () => {
    let filtered = tasks;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(task => 
        (task.video_name && task.video_name.toLowerCase().includes(term)) ||
        (task.client_name && task.client_name.toLowerCase().includes(term)) ||
        (task.operator_name && task.operator_name.toLowerCase().includes(term)) ||
        (task.editor_name && task.editor_name.toLowerCase().includes(term)) ||
        (task.video_type && task.video_type.toLowerCase().includes(term)) ||
        (task.notes && task.notes.toLowerCase().includes(term)) ||
        (task.status && task.status.toLowerCase().includes(term))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    setFilteredTasks(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  // Safe data access function
  const getTaskField = (task, field, defaultValue = 'N/A') => {
    const value = task[field];
    return value !== null && value !== undefined && value !== '' ? value : defaultValue;
  };

  // Work on Task - Open Modal
  const handleWorkOnTask = (task) => {
    setSelectedTask(task);
    setWorkFormData({
      thumbnail_link: getTaskField(task, 'thumbnail_link', ''),
      designer_notes: getTaskField(task, 'designer_notes', '')
    });
    setShowWorkModal(true);
  };

  // View Detail - Navigate to task details
  const handleViewDetail = (task) => {
    navigate('/task-details', { 
      state: { 
        task: {
          id: getTaskField(task, 'id'),
          videoName: getTaskField(task, 'video_name'),
          client: getTaskField(task, 'client_name'),
          editor: getTaskField(task, 'editor_name'),
          designer: getTaskField(task, 'designer_name'),
          videoType: getTaskField(task, 'video_type'),
          status: getTaskField(task, 'status'),
          rawLink: getTaskField(task, 'raw_link'),
          editorLink: getTaskField(task, 'edited_video_link'),
          thumbnailLink: getTaskField(task, 'thumbnail_link'),
          designerNotes: getTaskField(task, 'designer_notes'),
          shootDate: getTaskField(task, 'shoot_date'),
          videoCount: getTaskField(task, 'video_count'),
          operatorName: getTaskField(task, 'operator_name'),
          assignedByName: getTaskField(task, 'assigned_by_name'),
          referenceVideoLink: getTaskField(task, 'reference_video_link'),
          notes: getTaskField(task, 'notes'),
          editorNotes: getTaskField(task, 'editor_notes')
        }
      } 
    });
  };

  // Add Task - Navigate to AddTask page
  const handleAddTask = () => {
    navigate('/designer-tasks');
  };

  // Close Modal
  const handleCloseModal = () => {
    setShowWorkModal(false);
    setSelectedTask(null);
    setWorkFormData({
      thumbnail_link: '',
      designer_notes: ''
    });
  };

  // Handle Work Form Input Change
  const handleWorkFormChange = (e) => {
    const { name, value } = e.target;
    setWorkFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Submit Designer Work
  const handleSubmitWork = async () => {
    if (!selectedTask) return;

    try {
      setIsSubmitting(true);
      
      const response = await fetch(`${API_BASE}/update_designer_task.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_id: getTaskField(selectedTask, 'id'),
          thumbnail_link: workFormData.thumbnail_link,
          designer_notes: workFormData.designer_notes,
          status: 'pending'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage('✅ Designer work submitted successfully!');
        handleCloseModal();
        fetchDesignerTasks();
      } else {
        setMessage('❌ ' + (data.message || 'Error submitting work'));
      }
    } catch (error) {
      console.error('Error submitting designer work:', error);
      setMessage('❌ Error submitting work: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLinkClick = (link) => {
    if (link && link !== '-' && link !== 'NULL' && link !== 'null' && link !== 'N/A') {
      let formattedLink = link;
      if (!formattedLink.startsWith('http://') && !formattedLink.startsWith('https://')) {
        formattedLink = 'https://' + formattedLink;
      }
      window.open(formattedLink, '_blank', 'noopener,noreferrer');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'bg-warning text-dark', text: 'Pending', icon: 'fa-clock' },
      in_progress: { class: 'bg-info text-white', text: 'In Progress', icon: 'fa-spinner' },
      completed: { class: 'bg-success text-white', text: 'Completed', icon: 'fa-check' },
      cancelled: { class: 'bg-danger text-white', text: 'Cancelled', icon: 'fa-times' },
      assigned: { class: 'bg-primary text-white', text: 'Assigned', icon: 'fa-user-check' },
      needs_revision: { class: 'bg-warning text-dark', text: 'Needs Revision', icon: 'fa-sync' },
      approved: { class: 'bg-success text-white', text: 'Approved', icon: 'fa-check-circle' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`badge ${config.class}`} style={{fontSize: '0.7rem'}}>
        <i className={`fas ${config.icon} me-1`}></i>
        {config.text}
      </span>
    );
  };

  const safeFormatDate = (dateString) => {
    if (!dateString || dateString === 'NULL' || dateString === '0000-00-00' || dateString === 'N/A') {
      return 'N/A';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const truncateText = (text, maxLength = 15) => {
    if (!text || text === '-' || text === 'NULL' || text === 'null' || text === 'N/A') return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getFullText = (text) => {
    if (!text || text === '-' || text === 'NULL' || text === 'null' || text === 'N/A') return 'N/A';
    return text;
  };

  const isLinkAvailable = (link) => {
    return link && link !== '-' && link !== 'NULL' && link !== 'null' && link !== 'N/A' && link.trim() !== '';
  };

  // SIMPLIFIED STYLES
  const styles = {
    container: {
      minHeight: '100vh',
      background: colors.backgroundDark,
      position: 'relative',
      overflowX: 'auto'
    },
    header: {
      padding: '20px',
      background: colors.backgroundDark,
    },
    headerContent: {
      maxWidth: '100%',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '15px'
    },
    headerLeft: {
      flex: 1,
      minWidth: '250px'
    },
    headerRight: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap'
    },
    pageTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: 'white',
      margin: '0 0 10px 0'
    },
    pageSubtitle: {
      color: colors.textLight,
      fontSize: '0.9rem',
      margin: '0 0 15px 0'
    },
    addTaskButton: {
      background: colors.success,
      color: 'white',
      border: 'none',
      padding: '10px 16px',
      borderRadius: '6px',
      fontWeight: '600',
      fontSize: '0.85rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap',
      height: 'fit-content'
    },
    addTaskButtonHover: {
      background: '#0da371',
      transform: 'translateY(-1px)'
    },
    messageAlert: {
      background: 'rgba(220, 53, 69, 0.1)',
      color: 'white',
      padding: '10px 15px',
      borderRadius: '6px',
      margin: '0 20px 15px',
      border: `1px solid ${colors.danger}`,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.8rem'
    },
    content: {
      padding: '0 20px 20px',
      maxWidth: '100%',
      margin: '0 auto'
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      border: `1px solid ${colors.borderDark}`,
      marginBottom: '15px'
    },
    cardHeader: {
      background: colors.primary,
      color: 'white',
      padding: '12px 15px',
      borderRadius: '8px 8px 0 0'
    },
    cardTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      margin: 0
    },
    cardBody: {
      padding: '15px'
    },
    formGroup: {
      marginBottom: '15px'
    },
    formLabel: {
      color: 'white',
      fontWeight: '600',
      fontSize: '0.8rem',
      marginBottom: '5px',
      display: 'block'
    },
    formInput: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: `1px solid ${colors.borderDark}`,
      borderRadius: '6px',
      padding: '8px 12px',
      color: 'white',
      fontSize: '0.8rem',
      width: '100%'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr auto',
      gap: '10px',
      alignItems: 'end'
    },
    submitBtn: {
      background: colors.primary,
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '6px',
      fontWeight: '600',
      fontSize: '0.8rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px'
    },
    refreshBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '6px 12px',
      borderRadius: '6px',
      fontWeight: '600',
      fontSize: '0.8rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginBottom: '15px'
    },
    dropdownSelect: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: `1px solid ${colors.borderDark}`,
      borderRadius: '6px',
      padding: '8px 12px',
      color: 'white',
      fontSize: '0.8rem',
      width: '100%'
    },
    // Table header styles - White text for specific columns
    tableHeader: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      borderBottom: `1px solid ${colors.borderDark}`,
      padding: '8px 6px',
      fontWeight: '600',
      fontSize: '0.75rem',
      whiteSpace: 'nowrap'
    },
    // Regular header for other columns
    regularTableHeader: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: colors.textLight,
      borderBottom: `1px solid ${colors.borderDark}`,
      padding: '8px 6px',
      fontWeight: '600',
      fontSize: '0.75rem',
      whiteSpace: 'nowrap'
    },
    tableRow: {
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      color: 'white'
    },
    tableCell: {
      padding: '6px 4px',
      verticalAlign: 'middle',
      fontSize: '0.7rem',
      whiteSpace: 'nowrap',
      position: 'relative'
    },
    // White text cell style for specific columns
    whiteTextCell: {
      color: colors.whiteText,
      fontWeight: '600'
    },
    // Regular cell style for other columns
    regularCell: {
      color: colors.textLight,
      fontWeight: '500'
    },
    loadingState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      color: 'white',
      textAlign: 'center'
    },
    spinner: {
      width: '16px',
      height: '16px',
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    // Modal Styles
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      background: colors.backgroundMedium,
      borderRadius: '12px',
      border: `1px solid ${colors.borderDark}`,
      width: '100%',
      maxWidth: '500px',
      maxHeight: '90vh',
      overflow: 'auto'
    },
    modalHeader: {
      background: colors.primary,
      color: 'white',
      padding: '15px 20px',
      borderRadius: '12px 12px 0 0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    modalTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      margin: 0
    },
    modalClose: {
      background: 'none',
      border: 'none',
      color: 'white',
      fontSize: '1.2rem',
      cursor: 'pointer',
      padding: '5px'
    },
    modalBody: {
      padding: '20px'
    },
    modalFooter: {
      padding: '15px 20px',
      borderTop: `1px solid ${colors.borderDark}`,
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end'
    },
    cancelBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '8px 16px',
      borderRadius: '6px',
      fontWeight: '600',
      fontSize: '0.8rem',
      cursor: 'pointer'
    },
    saveBtn: {
      background: colors.success,
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '6px',
      fontWeight: '600',
      fontSize: '0.8rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    // Tooltip style
    tooltip: {
      position: 'absolute',
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'rgba(0, 0, 0, 0.9)',
      color: 'white',
      padding: '8px 12px',
      borderRadius: '4px',
      fontSize: '0.75rem',
      whiteSpace: 'nowrap',
      zIndex: 1000,
      border: `1px solid ${colors.borderDark}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      pointerEvents: 'none'
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <h3 style={{fontSize: '1.2rem', margin: '15px 0 10px 0', fontWeight: '600', color: 'white'}}>
              Loading Designer Tasks
            </h3>
            <p style={{color: colors.textLight, margin: 0, fontSize: '0.9rem'}}>
              Please wait while we fetch your assigned tasks
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Header Section */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.headerLeft}>
              <h1 style={styles.pageTitle}>
                <i className="fas fa-palette me-2"></i>
                Designer Dashboard
              </h1>
              <p style={styles.pageSubtitle}>
                Manage your design tasks and submit thumbnails
                {tasks.length > 0 && (
                  <span style={{
                    background: colors.primary, 
                    color: 'white', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    marginLeft: '8px'
                  }}>
                    {tasks.length} tasks
                  </span>
                )}
              </p>
            </div>
            <div style={styles.headerRight}>
              <button 
                style={styles.addTaskButton}
                onClick={handleAddTask}
                onMouseEnter={(e) => e.target.style.background = styles.addTaskButtonHover.background}
                onMouseLeave={(e) => e.target.style.background = colors.success}
              >
                <i className="fas fa-plus-circle"></i>
                Add New Task
              </button>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div style={{
            ...styles.messageAlert,
            background: message.includes('✅') 
              ? 'rgba(16, 185, 129, 0.1)' 
              : 'rgba(220, 53, 69, 0.1)',
            border: message.includes('✅') 
              ? `1px solid ${colors.success}`
              : `1px solid ${colors.danger}`
          }}>
            <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
            <span>{message}</span>
            <button 
              onClick={() => setMessage('')} 
              style={{
                background: 'none', 
                border: 'none', 
                color: 'white', 
                cursor: 'pointer', 
                marginLeft: 'auto',
                fontSize: '0.8rem'
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div style={styles.content}>
          <button 
            style={styles.refreshBtn}
            onClick={fetchDesignerTasks}
            disabled={loading}
          >
            <i className={`fas fa-sync ${loading ? 'fa-spin' : ''} me-2`}></i>
            Refresh
          </button>

          {/* Search and Filter Section */}
          <div style={{marginBottom: '15px'}}>
            <div style={styles.formCard}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <i className="fas fa-filter me-2"></i>
                  Filter Tasks
                </h3>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      <i className="fas fa-search me-2"></i>
                      Search Tasks
                    </label>
                    <input
                      type="text"
                      placeholder="Search tasks..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={styles.formInput}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      <i className="fas fa-filter me-2"></i>
                      Filter by Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      style={styles.dropdownSelect}
                    >
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="needs_revision">Needs Revision</option>
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <button
                      onClick={clearFilters}
                      disabled={!searchTerm && statusFilter === 'all'}
                      style={{ 
                        ...styles.submitBtn,
                        opacity: (!searchTerm && statusFilter === 'all') ? 0.6 : 1,
                        cursor: (!searchTerm && statusFilter === 'all') ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <i className="fas fa-times me-2"></i>
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tasks Card */}
          <div style={styles.formCard}>
            <div style={styles.cardHeader}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <h3 style={styles.cardTitle}>
                  <i className="fas fa-list me-2"></i>
                  Design Tasks
                </h3>
                <small style={{color: 'rgba(255, 255, 255, 0.8)'}}>
                  {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''} found
                </small>
              </div>
            </div>
            <div style={{padding: '0'}}>
              {filteredTasks.length === 0 ? (
                <div style={{padding: '40px 20px', textAlign: 'center'}}>
                  <i className="fas fa-palette fa-3x mb-3" style={{color: `${colors.textLight}20`}}></i>
                  <h4 style={{color: colors.textLight, marginBottom: '10px', fontSize: '1rem'}}>No design tasks</h4>
                  <p style={{color: colors.textLight, marginBottom: '20px', fontSize: '0.8rem'}}>
                    {tasks.length === 0 
                      ? 'You don\'t have any design tasks assigned yet.' 
                      : 'No tasks match your search criteria.'}
                  </p>
                  <div style={{display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap'}}>
                    <button 
                      style={{
                        ...styles.addTaskButton,
                        background: colors.success,
                        padding: '8px 16px'
                      }}
                      onClick={handleAddTask}
                    >
                      <i className="fas fa-plus-circle me-2"></i>
                      Add New Task
                    </button>
                    {(searchTerm || statusFilter !== 'all') && (
                      <button 
                        onClick={clearFilters}
                        style={{
                          ...styles.submitBtn,
                          background: colors.primary,
                          padding: '8px 16px'
                        }}
                      >
                        <i className="fas fa-times me-2"></i>
                        Clear Filters
                      </button>
                    )}
                    <button 
                      onClick={fetchDesignerTasks}
                      style={{
                        ...styles.refreshBtn,
                        padding: '8px 16px'
                      }}
                    >
                      <i className="fas fa-sync me-2"></i>
                      Refresh
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{overflowX: 'auto', width: '100%'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse', minWidth: '1200px'}}>
                    <thead>
                      <tr>
                        {/* ID - White text */}
                        <th style={styles.tableHeader}>ID</th>
                        
                        {/* Video Name - White text */}
                        <th style={styles.tableHeader}>Video Name</th>
                        
                        {/* Client - White text */}
                        <th style={styles.tableHeader}>Client</th>
                        
                        {/* Operator - White text */}
                        <th style={styles.tableHeader}>Operator</th>
                        
                        {/* Editor - White text */}
                        <th style={styles.tableHeader}>Editor</th>
                        
                        {/* Video Type - Regular text */}
                        <th style={styles.regularTableHeader}>Video Type</th>
                        
                        {/* Status - Regular text */}
                        <th style={styles.regularTableHeader}>Status</th>
                        
                        {/* Raw Link - Regular text */}
                        <th style={styles.regularTableHeader}>Raw Link</th>
                        
                        {/* Edit Link - Regular text */}
                        <th style={styles.regularTableHeader}>Edit Link</th>
                        
                        {/* Reference Link - Regular text */}
                        <th style={styles.regularTableHeader}>Reference Link</th>
                        
                        {/* Notes - Regular text */}
                        <th style={styles.regularTableHeader}>Notes</th>
                        
                        {/* Thumbnail - Regular text */}
                        <th style={styles.regularTableHeader}>Thumbnail</th>
                        
                        {/* Designer Notes - Regular text */}
                        <th style={styles.regularTableHeader}>Designer Notes</th>
                        
                        {/* Shoot Date - Regular text */}
                        <th style={styles.regularTableHeader}>Shoot Date</th>
                        
                        {/* Action - Regular text */}
                        <th style={styles.regularTableHeader}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTasks.map((task, rowIndex) => {
                        const hasValidRawLink = isLinkAvailable(getTaskField(task, 'raw_link'));
                        const hasValidEditedLink = isLinkAvailable(getTaskField(task, 'edited_video_link'));
                        const hasValidReferenceLink = isLinkAvailable(getTaskField(task, 'reference_video_link'));
                        const hasValidThumbnail = isLinkAvailable(getTaskField(task, 'thumbnail_link'));
                        const isCompleted = getTaskField(task, 'status') === 'completed';
                        
                        return (
                          <tr key={getTaskField(task, 'id')} style={styles.tableRow}>
                            {/* ID - White text */}
                            <td style={{...styles.tableCell, ...styles.whiteTextCell, fontWeight: 'bold'}}
                                onMouseEnter={(e) => setHoveredCell({row: rowIndex, col: 'id', text: getFullText(getTaskField(task, 'id'))})}
                                onMouseLeave={() => setHoveredCell(null)}>
                              #{getTaskField(task, 'id')}
                              {hoveredCell && hoveredCell.row === rowIndex && hoveredCell.col === 'id' && (
                                <div style={styles.tooltip}>
                                  ID: {getFullText(getTaskField(task, 'id'))}
                                </div>
                              )}
                            </td>
                            
                            {/* Video Name - White text */}
                            <td style={{...styles.tableCell, ...styles.whiteTextCell}}
                                onMouseEnter={(e) => setHoveredCell({row: rowIndex, col: 'video_name', text: getFullText(getTaskField(task, 'video_name'))})}
                                onMouseLeave={() => setHoveredCell(null)}>
                              <button 
                                onClick={() => handleViewDetail(task)}
                                style={{ 
                                  color: colors.whiteText,
                                  fontWeight: '600', 
                                  background: 'none', 
                                  border: 'none', 
                                  cursor: 'pointer',
                                  padding: 0,
                                  textAlign: 'left',
                                  fontSize: '0.7rem'
                                }}
                              >
                                {truncateText(getTaskField(task, 'video_name'), 12)}
                              </button>
                              {hoveredCell && hoveredCell.row === rowIndex && hoveredCell.col === 'video_name' && (
                                <div style={styles.tooltip}>
                                  Video Name: {getFullText(getTaskField(task, 'video_name'))}
                                </div>
                              )}
                            </td>
                            
                            {/* Client - White text */}
                            <td style={{...styles.tableCell, ...styles.whiteTextCell}}
                                onMouseEnter={(e) => setHoveredCell({row: rowIndex, col: 'client', text: getFullText(getTaskField(task, 'client_name'))})}
                                onMouseLeave={() => setHoveredCell(null)}>
                              {truncateText(getTaskField(task, 'client_name'), 10)}
                              {hoveredCell && hoveredCell.row === rowIndex && hoveredCell.col === 'client' && (
                                <div style={styles.tooltip}>
                                  Client: {getFullText(getTaskField(task, 'client_name'))}
                                </div>
                              )}
                            </td>
                            
                            {/* Operator - White text */}
                            <td style={{...styles.tableCell, ...styles.whiteTextCell}}
                                onMouseEnter={(e) => setHoveredCell({row: rowIndex, col: 'operator', text: getFullText(getTaskField(task, 'operator_name'))})}
                                onMouseLeave={() => setHoveredCell(null)}>
                              {truncateText(getTaskField(task, 'operator_name'), 10)}
                              {hoveredCell && hoveredCell.row === rowIndex && hoveredCell.col === 'operator' && (
                                <div style={styles.tooltip}>
                                  Operator: {getFullText(getTaskField(task, 'operator_name'))}
                                </div>
                              )}
                            </td>
                            
                            {/* Editor - White text */}
                            <td style={{...styles.tableCell, ...styles.whiteTextCell}}
                                onMouseEnter={(e) => setHoveredCell({row: rowIndex, col: 'editor', text: getFullText(getTaskField(task, 'editor_name'))})}
                                onMouseLeave={() => setHoveredCell(null)}>
                              {truncateText(getTaskField(task, 'editor_name'), 10)}
                              {hoveredCell && hoveredCell.row === rowIndex && hoveredCell.col === 'editor' && (
                                <div style={styles.tooltip}>
                                  Editor: {getFullText(getTaskField(task, 'editor_name'))}
                                </div>
                              )}
                            </td>
                            
                            {/* Video Type - Regular text */}
                            <td style={{...styles.tableCell, ...styles.regularCell}}
                                onMouseEnter={(e) => setHoveredCell({row: rowIndex, col: 'video_type', text: getFullText(getTaskField(task, 'video_type'))})}
                                onMouseLeave={() => setHoveredCell(null)}>
                              {truncateText(getTaskField(task, 'video_type'), 8)}
                              {hoveredCell && hoveredCell.row === rowIndex && hoveredCell.col === 'video_type' && (
                                <div style={styles.tooltip}>
                                  Video Type: {getFullText(getTaskField(task, 'video_type'))}
                                </div>
                              )}
                            </td>
                            
                            {/* Status - Regular */}
                            <td style={styles.tableCell}>
                              {getStatusBadge(getTaskField(task, 'status'))}
                            </td>
                            
                            {/* Raw Link - Regular */}
                            <td style={styles.tableCell}>
                              <button 
                                style={{
                                  background: hasValidRawLink ? colors.primary : 'rgba(255, 255, 255, 0.1)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  cursor: hasValidRawLink ? 'pointer' : 'not-allowed',
                                  opacity: hasValidRawLink ? 1 : 0.6,
                                  fontSize: '0.6rem',
                                  fontWeight: '600'
                                }}
                                onClick={() => handleLinkClick(getTaskField(task, 'raw_link'))}
                                disabled={!hasValidRawLink}
                              >
                                {hasValidRawLink ? 'View' : 'N/A'}
                              </button>
                            </td>
                            
                            {/* Edit Link - Regular */}
                            <td style={styles.tableCell}>
                              <button 
                                style={{
                                  background: hasValidEditedLink ? colors.success : 'rgba(255, 255, 255, 0.1)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  cursor: hasValidEditedLink ? 'pointer' : 'not-allowed',
                                  opacity: hasValidEditedLink ? 1 : 0.6,
                                  fontSize: '0.6rem',
                                  fontWeight: '600'
                                }}
                                onClick={() => handleLinkClick(getTaskField(task, 'edited_video_link'))}
                                disabled={!hasValidEditedLink}
                              >
                                {hasValidEditedLink ? 'View' : 'N/A'}
                              </button>
                            </td>
                            
                            {/* Reference Link - Regular */}
                            <td style={styles.tableCell}>
                              <button 
                                style={{
                                  background: hasValidReferenceLink ? colors.warning : 'rgba(255, 255, 255, 0.1)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  cursor: hasValidReferenceLink ? 'pointer' : 'not-allowed',
                                  opacity: hasValidReferenceLink ? 1 : 0.6,
                                  fontSize: '0.6rem',
                                  fontWeight: '600'
                                }}
                                onClick={() => handleLinkClick(getTaskField(task, 'reference_video_link'))}
                                disabled={!hasValidReferenceLink}
                              >
                                {hasValidReferenceLink ? 'View' : 'N/A'}
                              </button>
                            </td>
                            
                            {/* Notes - Regular text */}
                            <td style={{...styles.tableCell, ...styles.regularCell}}
                                onMouseEnter={(e) => setHoveredCell({row: rowIndex, col: 'notes', text: getFullText(getTaskField(task, 'notes'))})}
                                onMouseLeave={() => setHoveredCell(null)}>
                              {truncateText(getTaskField(task, 'notes'), 10)}
                              {hoveredCell && hoveredCell.row === rowIndex && hoveredCell.col === 'notes' && (
                                <div style={styles.tooltip}>
                                  Notes: {getFullText(getTaskField(task, 'notes'))}
                                </div>
                              )}
                            </td>
                            
                            {/* Thumbnail - Regular */}
                            <td style={styles.tableCell}>
                              <button 
                                style={{
                                  background: hasValidThumbnail ? colors.accent : 'rgba(255, 255, 255, 0.1)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  cursor: hasValidThumbnail ? 'pointer' : 'not-allowed',
                                  opacity: hasValidThumbnail ? 1 : 0.6,
                                  fontSize: '0.6rem',
                                  fontWeight: '600'
                                }}
                                onClick={() => handleLinkClick(getTaskField(task, 'thumbnail_link'))}
                                disabled={!hasValidThumbnail}
                              >
                                {hasValidThumbnail ? 'View' : 'N/A'}
                              </button>
                            </td>
                            
                            {/* Designer Notes - Regular text */}
                            <td style={{...styles.tableCell, ...styles.regularCell}}
                                onMouseEnter={(e) => setHoveredCell({row: rowIndex, col: 'designer_notes', text: getFullText(getTaskField(task, 'designer_notes'))})}
                                onMouseLeave={() => setHoveredCell(null)}>
                              {truncateText(getTaskField(task, 'designer_notes'), 10)}
                              {hoveredCell && hoveredCell.row === rowIndex && hoveredCell.col === 'designer_notes' && (
                                <div style={styles.tooltip}>
                                  Designer Notes: {getFullText(getTaskField(task, 'designer_notes'))}
                                </div>
                              )}
                            </td>
                            
                            {/* Shoot Date - Regular text */}
                            <td style={{...styles.tableCell, ...styles.regularCell}}
                                onMouseEnter={(e) => setHoveredCell({row: rowIndex, col: 'shoot_date', text: getFullText(safeFormatDate(getTaskField(task, 'shoot_date')))})}
                                onMouseLeave={() => setHoveredCell(null)}>
                              {safeFormatDate(getTaskField(task, 'shoot_date'))}
                              {hoveredCell && hoveredCell.row === rowIndex && hoveredCell.col === 'shoot_date' && (
                                <div style={styles.tooltip}>
                                  Shoot Date: {getFullText(safeFormatDate(getTaskField(task, 'shoot_date')))}
                                </div>
                              )}
                            </td>
                            
                            {/* Action - Regular */}
                            <td style={styles.tableCell}>
                              {isCompleted ? (
                                <button
                                  style={{
                                    background: colors.success,
                                    color: 'white',
                                    border: 'none',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    cursor: 'default',
                                    fontWeight: '600',
                                    fontSize: '0.7rem',
                                    opacity: 0.8
                                  }}
                                  disabled
                                >
                                  <i className="fas fa-check me-1"></i>
                                  Completed
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleWorkOnTask(task)}
                                  style={{
                                    background: colors.primary,
                                    color: 'white',
                                    border: 'none',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '0.7rem'
                                  }}
                                >
                                  <i className="fas fa-edit me-1"></i>
                                  Work
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Work Modal */}
      {showWorkModal && selectedTask && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                <i className="fas fa-palette me-2"></i>
                Design Work - {getTaskField(selectedTask, 'video_name')}
              </h3>
              <button style={styles.modalClose} onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  <i className="fas fa-image me-2"></i>
                  Thumbnail Link *
                </label>
                <input
                  type="url"
                  name="thumbnail_link"
                  value={workFormData.thumbnail_link}
                  onChange={handleWorkFormChange}
                  placeholder="https://drive.google.com/thumbnail-link"
                  required
                  style={styles.formInput}
                />
                <div style={{color: colors.textLight, fontSize: '0.7rem', marginTop: '5px'}}>
                  Enter the Google Drive or image link for the thumbnail
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  <i className="fas fa-sticky-note me-2"></i>
                  Designer Notes (Optional)
                </label>
                <textarea
                  name="designer_notes"
                  value={workFormData.designer_notes}
                  onChange={handleWorkFormChange}
                  rows="4"
                  placeholder="Add any notes about the design work (optional)..."
                  style={{...styles.formInput, resize: 'vertical'}}
                ></textarea>
              </div>

              <div style={{background: 'rgba(255, 255, 255, 0.05)', padding: '10px', borderRadius: '6px', marginTop: '15px'}}>
                <h4 style={{color: colors.textLight, fontSize: '0.8rem', margin: '0 0 8px 0'}}>Task Details:</h4>
                <div style={{color: colors.textLight, fontSize: '0.7rem'}}>
                  <div><strong>Client:</strong> {getTaskField(selectedTask, 'client_name')}</div>
                  <div><strong>Video Type:</strong> {getTaskField(selectedTask, 'video_type')}</div>
                  <div><strong>Shoot Date:</strong> {safeFormatDate(getTaskField(selectedTask, 'shoot_date'))}</div>
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={handleCloseModal}>
                Cancel
              </button>
              <button 
                style={styles.saveBtn}
                onClick={handleSubmitWork}
                disabled={isSubmitting || !workFormData.thumbnail_link}
              >
                {isSubmitting ? (
                  <>
                    <div style={styles.spinner}></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check me-1"></i>
                    Submit Work
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Remove white background from dropdown */
        select option {
          background: #302b63;
          color: white;
        }
        
        select:focus {
          outline: none;
          border-color: #6366F1;
        }
      `}</style>
    </Layout>
  );
}

export default DesignerDashboard;